// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Declare default config for epdm calculator instances.
 * @author James Reid
 */

// @ts-check

// @@imports-types
import {
    Config,
    MEMBRANES,
    JOIN_TYPES,
    SUBSTRATE_TYPES,
    TRIM_COLORS,
    TRIM_DEPTHS,
    TRIM_TYPES,
    UNIT_TYPES,
    SEAM_DIRECTIONS
} from "../types/index.js"

// @@body
// Please note that as with all other measurements in this calculator, all
// relevant values below are in SI units (i.e. metres, kg etc.) unless otherwise
// specified.
/** @type {Config} */
const config = {
    adhesiveCoverages: {
        // Note that all deck area coverages consider covering *both* sides of
        // the membrane with the exception of pu adhesive, which has a single
        // sided coverage rate due to method of application.
        deck: {
            waterBased: {
                default: 4,
                min: 3,
                max: 4,
                client: 4,
                unitType: UNIT_TYPES.area
            },
            contactBonding: {
                default: 2,
                min: 1,
                max: 2,
                client: 2,
                unitType: UNIT_TYPES.area
            },
            pu: {
                default: 3.3,
                min: 2.8,
                max: 3.7,
                client: 3.3,
                unitType: UNIT_TYPES.area
            }
        },
        primer: {
            default: 4,
            min: 3.7,
            max: 6.6,
            client: 4,
            unitType: UNIT_TYPES.area
        },
        sealants: {
            lapEdge: {
                default: 18.2,
                min: 18.2,
                max: 18.2,
                client: 18.2,
                unitType: UNIT_TYPES.length
            },
            waterBlock: {
                default: 9.1,
                min: 9.1,
                max: 9.1,
                client: 9.1,
                unitType: UNIT_TYPES.length
            },
            msPolymer: {
                default: 1,
                min: 1,
                max: 1,
                client: 1,
                unitType: UNIT_TYPES.volume
            },
            blackSilicone: {
                default: 18.2,
                min: 18.2,
                max: 18.2,
                client: 18.2,
                unitType: UNIT_TYPES.length
            },
        }
    },
    clientLimits: {
        roll: {
            width: Infinity,
            weight: Infinity,
            waste: {
                upper: 1.125,
                lower: 0.125
            }
        },
        adhesive: {
            waste: {
                upper: 2.5,
                lower: 1
            }
        }
    },
    overhangs: {
        houseWall: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        parapetWall: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        gutterDrip: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        raisedKerb: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        pitchRoofOn: {
            default: 0.30,
            min: 0.30,
            client: 0.30
        },
        pitchRoofOff: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        feltJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        seamJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        buttJoin: {
            default: 0.15,
            min: 0,
            client: 0.15
        },
        fleeceBackedJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        none: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        default: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        }
    },
    parapet: {
        width: 0.23,
        height: 0.5
    },
    membrane: MEMBRANES.onePiece_1_20,
    seamDirection: SEAM_DIRECTIONS.any,
    substrate: SUBSTRATE_TYPES.osb,
    rubberJoin: JOIN_TYPES.seam,
    contactBorderDepth: 0.15,
    tapes: {
        overlap: 0.075,
        primerBorder: 0.01,
        addSealant: false,
        precutPatches: true
    },
    extraAllowances: {
        tape: 1,
        adhesive: 1
    },
    trims: {
        type: TRIM_TYPES.cellular,
        color: TRIM_COLORS.black,
        depth: TRIM_DEPTHS.standard
    }
}

// @@exports
export { config }
